import { createTheme, PaletteOptions } from '@mui/material/styles'
import { grey } from '@mui/material/colors'

const dark1 = true

const grey50 = '#fafafa'
const darkBackground = dark1 ? '#181b1f' : '#1b222a'
const darkPaper = dark1 ? '#1c2025' : '#1e1e1e' // '#2c323a'
const darkZebraBackground = dark1 ? '#282e36' : '#252525' // '#343b45'

declare module '@mui/material/styles' {
  interface Palette {
    darkHeader: string
    header: string
  }

  interface PaletteOptions {
    darkHeader?: string
    header?: string
  }

  interface Theme {
    dataTable: {
      standardCellPadding: string
      standardZebraBackground: string
    }
    layout: {
      sidebarWidth: string
    }
    toolbar: {
      bottomSpacing: number
      buttonHeight: number
      darkHeaderBackground: string
      minHeight: number
    }
  }

  interface ThemeOptions {
    dataTable?: {
      standardCellPadding?: string
      standardZebraBackground?: string
    }
    layout?: {
      sidebarWidth?: string
    }
    toolbar?: {
      bottomSpacing?: number
      buttonHeight?: number
      darkHeaderBackground?: string
      minHeight?: number
    }
  }
}

const lightPrimary = {
  light: '#EFF0F2',
  main: '#60aae5',
  dark: '#2962FF',
  contrastText: '#FFF',
}

const darkPrimary = {
  light: '#8FCBFF',
  main: '#38A4EC',
  dark: '#2A9AE6',
  contrastText: '#FFF',
}

const sharedPalette = {
  secondary: {
    light: '#bf9ac7',
    main: '#8135A3',
    dark: '#5A1E74',
    contrastText: '#FFF',
  },
  success: {
    main: '#94ba0a',
  },
  error: {
    main: '#E43D3D',
  },
  warning: {
    main: '#e18b40',
    contrastText: '#FFF',
  },
  info: {
    main: '#0288d1',
  },
}

export const colors: PaletteOptions = {
  ...sharedPalette,
  primary: lightPrimary,
  header: '#EFF0F2',
  divider: '#e0e0e0',
  darkHeader: '#455161',
  background: {
    default: grey50,
    paper: '#ffffff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  grey: {
    ...grey,
    50: grey50,
  },
}

const darkColors: PaletteOptions = {
  ...sharedPalette,
  primary: darkPrimary,
  header: '#2D3540',
  divider: 'rgba(255, 255, 255, 0.12)',
  darkHeader: '#1F2933',
  background: {
    default: darkBackground,
    paper: darkPaper,
  },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    disabled: 'rgba(255, 255, 255, 0.5)',
  },
}

const headingDefaultFontSize = 2

const sharedThemeOptions = {
  spacing: 8,
  typography: {
    fontFamily: ['"Open Sans"', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    htmlFontSize: 10,
    h1: {
      fontSize: `${headingDefaultFontSize}rem`,
    },
    h2: {
      fontSize: `${0.9 * headingDefaultFontSize}rem`,
    },
    h3: {
      fontSize: `${0.8 * headingDefaultFontSize}rem`,
    },
    h4: {
      fontSize: `${0.7 * headingDefaultFontSize}rem`,
    },
    h5: {
      fontSize: `${0.65 * headingDefaultFontSize}rem`,
    },
    h6: {
      fontSize: `${0.6 * headingDefaultFontSize}rem`,
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: `1.8rem`,
    },
    subtitle2: {
      fontSize: `1.4rem`,
    },
    body1: {
      fontSize: `1.4rem`,
    },
    body2: {
      fontSize: `1.2rem`,
    },
    caption: {
      fontSize: '1.1rem',
    },
  },
  shape: {
    borderRadius: 6,
  },
  layout: {
    sidebarWidth: '22rem',
  },
} as const

export const theme = createTheme({
  ...sharedThemeOptions,
  colorSchemes: {
    light: {
      palette: colors,
      dataTable: {
        standardCellPadding: '10px 12px',
        standardZebraBackground: grey50,
      },
      toolbar: {
        bottomSpacing: 2,
        buttonHeight: 34,
        darkHeaderBackground: 'rgba(239, 240, 242, 0.55)',
        minHeight: 40,
      },
    },
    dark: {
      palette: darkColors,
      dataTable: {
        standardCellPadding: '10px 12px',
        standardZebraBackground: darkZebraBackground,
      },
      toolbar: {
        bottomSpacing: 2,
        buttonHeight: 34,
        darkHeaderBackground: 'rgba(45, 53, 64, 0.55)',
        minHeight: 40,
      },
    },
  },
})

/** Light-only theme for logged-out routes (no color scheme switching). */
export const lightTheme = createTheme({
  ...sharedThemeOptions,
  palette: colors,
  dataTable: {
    standardCellPadding: '10px 12px',
    standardZebraBackground: grey50,
  },
  toolbar: {
    bottomSpacing: 2,
    buttonHeight: 34,
    darkHeaderBackground: 'rgba(239, 240, 242, 0.55)',
    minHeight: 40,
  },
})
